import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputField } from 'src/components/atoms/input-field';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { useUpdateSpaceGroupName } from 'src/features/space-groups/api/hooks/use-update-space-group-name';
import { z } from 'zod';
import { SpaceGroupsActionsSlotPayload } from '../../../molecules/space-groups-table-row';
import { useUpdateSpaceGroupNameFormValidationSchema } from './schema/use-form-validation-schema';

interface Props extends DialogActions {
  selectedSpaceGroup: SpaceGroupsActionsSlotPayload | null;
}

export const UpdateSpaceGroupNameDialog = ({
  open,
  onCancel,
  selectedSpaceGroup,
}: Props) => {
  const { t } = useTranslation(['spaceGroups']);

  const formValidationSchema = useUpdateSpaceGroupNameFormValidationSchema();

  type FormValues = z.infer<typeof formValidationSchema>;

  const defaultValues: FormValues = {
    name: '',
  };

  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(formValidationSchema),
    defaultValues,
    mode: 'onBlur',
  });

  const handleOnCancel = () => {
    onCancel?.();
    reset(defaultValues);
  };

  const { mutate: updateSpaceGroupName, isPending: isUpdatingSpaceGroupName } =
    useUpdateSpaceGroupName(handleOnCancel);

  useEffect(() => {
    if (selectedSpaceGroup) {
      reset({
        name: selectedSpaceGroup.name,
      });
    }
  }, [selectedSpaceGroup]);

  const handleUpdateSpaceGroupName = (data: FormValues) => {
    if (selectedSpaceGroup) {
      updateSpaceGroupName({
        token: selectedSpaceGroup.token,
        name: data.name,
      });
    }
  };

  return (
    <Dialog
      open={open}
      title={t('spaceGroups:common.editNameDialog.title')}
      onCancel={handleOnCancel}
      onConfirm={handleSubmit(handleUpdateSpaceGroupName)}
      isConfirmButtonSubmitting={isUpdatingSpaceGroupName}>
      <Controller
        control={control}
        name="name"
        render={({
          field: { name, value, onChange, onBlur },
          fieldState: { error, invalid },
        }) => (
          <InputField
            name={name}
            label={t('spaceGroups:common.editNameDialog.name')}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            error={invalid}
            helperText={error?.message}
            required
            fullWidth
          />
        )}
      />
    </Dialog>
  );
};
